import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Slider,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Pause,
  PlayArrow,
} from '@mui/icons-material';
import { Message } from './types';
import { audioService } from './audioService';
interface ChatAudioMessageProps {
  msg: Message;
  onDone?: () => void;
}

const ChatAudioMessage: React.FC<ChatAudioMessageProps> = ({
  msg,
  onDone
}) => {
  const [playing, setPlaying] = useState(false);
  const [showTranscription, setShowTranscription] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const textTranscribed = msg.text || 'Transcrição não disponível';

  const playAudio = () => {
    if (playing) {
      audioService.pause();
    } else {
      audioService.play(msg.audioUrl!);
    }
  };
  
  useEffect(() => {
    if (onDone) {
      onDone();
    }
  }, [onDone]);

  useEffect(() => {
    if (!msg.audioUrl) return;

    if (audioService.canAutoplay()) {
      const current = audioService.getCurrentAudio();
      if (current && current.src !== msg.audioUrl) {
        audioService.stop();
      }

      audioService.play(msg.audioUrl);
    }
  }, [msg.audioUrl]);

  useEffect(() => {
    const handlePlay = () => {
      if (audioService.getCurrentAudio()?.src === msg.audioUrl) {
        setPlaying(true);
      }
    };

    const handleStop = () => {
      setPlaying(false);
      setCurrentTime(0);
    };

    audioService.on('play', handlePlay);
    audioService.on('stop', handleStop);

    return () => {};
  }, [msg.audioUrl]);

  useEffect(() => {
    const unsubscribe = audioService.on(
      'timeupdate',
      (time: number, dur: number) => {
        const current = audioService.getCurrentAudio();
        if (current && current.src === msg.audioUrl) {
          setCurrentTime(time);
          setDuration(dur);
        }
      }
    );

    return () => unsubscribe();
  }, [msg.audioUrl]);

  useEffect(() => {
    const audio = audioService.getCurrentAudio();
    if (!audio) return;

    const handleEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('ended', handleEnded);
    };
  }, [msg.audioUrl]);

  const handleSeekValue = (value: number | number[]) => {
    const audio = audioService.getCurrentAudio();
    if (!audio) return;
    const newTime = Array.isArray(value) ? value[0] : value;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (seconds: number) => {
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60)
      .toString()
      .padStart(2, '0');
    return `${min}:${sec}`;
  };

  useEffect(() => {
    const currentAudio = audioService.getCurrentAudio();
    if (currentAudio && currentAudio.src === msg.audioUrl) {
      setCurrentTime(currentAudio.currentTime);
      setDuration(currentAudio.duration || 0);
      setPlaying(!currentAudio.paused);
    } else {
      setCurrentTime(0);
      setDuration(0);
      setPlaying(false);
    }
  }, [msg.audioUrl]);

  return (
    <Box
      sx={{
        maxWidth: { xs: '90%', sm: '94%' },
        width: '100%',
        ml: msg.from === 'user' ? 'auto' : 0,
        mr: msg.from === 'user' ? 0 : 'auto',
        px: 1,
        display: 'flex',
        alignItems: 'flex-end',
        flexDirection: msg.from === 'user' ? 'row-reverse' : 'row',
        gap: 1,
      }}
    >
      <Avatar
        src={
          msg.from === 'user'
            ? '/images/avatar-user.svg'
            : '/images/avatar-lis.png'
        }
        alt={msg.from === 'user' ? 'Usuário' : 'Lis'}
        sx={{
          width: 24,
          height: 24,
        }}
      />
      <Box
        sx={{
          bgcolor: msg.from === 'user' ? '#D6D7E0' : 'white',
          borderRadius: '12px',
          borderBottomLeftRadius: msg.from === 'user' ? '12px' : 0,
          borderBottomRightRadius: msg.from === 'user' ? 0 : '12px',
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1, sm: 1.5 },
          position: 'relative',
          fontSize: { xs: 14, sm: 16 },
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          maxWidth: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton color={'primary'} size="large" onClick={playAudio}>
              {playing ? <Pause /> : <PlayArrow />}
            </IconButton>
            <Slider
              min={0}
              max={duration}
              value={currentTime}
              step={0.1}
              onChange={(e, value) => handleSeekValue(value)}
              color="primary"
              sx={{
                width: '10rem',
                flex: 1,
                height: 4,
                padding: 0,
                color: 'primary.main',
                '& .MuiSlider-thumb': {
                  width: 10,
                  height: 10,
                },
              }}
            />
            <span style={{ fontSize: 12 }}>{formatTime(currentTime)}</span>
          </Box>
          <Button
            sx={{
              textTransform: 'none',
              alignSelf: 'flex-start',
              width: 'fit-content',
              padding: '6px 12px',
            }}
            onClick={() => setShowTranscription(!showTranscription)}
          >
            {showTranscription ? (
              <>
                <span>Ocultar</span>
                <KeyboardArrowUp style={{ fontSize: 16 }} color="primary" />
              </>
            ) : (
              <>
                <span>Transcrição</span>
                <KeyboardArrowDown style={{ fontSize: 16 }} color="primary" />
              </>
            )}
          </Button>
        </Box>
        {showTranscription && (
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: '#333',
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: 14, sm: 16 },
            }}
          >
            {textTranscribed}
          </Typography>
        )}

        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            textAlign: 'right',
            display: 'block',
            fontWeight: 500,
            fontSize: '13px',
            lineHeight: '18px',
            letterSpacing: '0px',
            color: '#475569',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {msg.time}
        </Typography>
      </Box>
    </Box>
  );
};

export default React.memo(ChatAudioMessage);
